import { api } from "@/lib/api/client";
import type { RegistrationResponseJSON } from "@simplewebauthn/browser";
import type { components } from "@/types/api";
import type { StepUpProof } from "./stepup.api";

export type Passkey = components["schemas"]["PasskeyCredential"];
export type PasskeyListResponse = components["schemas"]["PasskeyListResponse"] & {
  recoveryCodesUnseen?: boolean;
  recoveryCodesGeneration?: number;
};
export type PasskeyRegisterStartResponse = components["schemas"]["PasskeyRegisterStartResponse"];
export type PasskeyRegisterVerifyResponse =
  components["schemas"]["PasskeyRegisterVerifyResponse"] & {
    recoveryCodesGeneration?: number;
  };
export type DeletePasskeyResponse = components["schemas"]["DeletePasskeyResponse"];
export type RecoveryCodesResponse = components["schemas"]["RecoveryCodesResponse"] & {
  recoveryCodesGeneration?: number;
};
export type RecoveryCodesAckResponse = { acknowledged: boolean };

export function listPasskeys(): Promise<PasskeyListResponse> {
  return api.get<PasskeyListResponse>("/v1/mfa/passkeys");
}

export function acknowledgeRecoveryCodes(generation: number): Promise<RecoveryCodesAckResponse> {
  return api.post<RecoveryCodesAckResponse>("/v1/mfa/recovery-codes/ack", { generation });
}

export function startPasskeyRegistration(): Promise<PasskeyRegisterStartResponse> {
  return api.post<PasskeyRegisterStartResponse>("/v1/mfa/passkeys/register/start", undefined, {
    retrySafe: true,
  });
}

export function verifyPasskeyRegistration(
  response: RegistrationResponseJSON,
  name?: string,
  stepUp?: StepUpProof,
): Promise<PasskeyRegisterVerifyResponse> {
  return api.post<PasskeyRegisterVerifyResponse>("/v1/mfa/passkeys/register/verify", {
    response,
    ...(name ? { name } : {}),
    ...(stepUp ? { stepUp } : {}),
  });
}

export function renamePasskey(passkeyId: string, name: string): Promise<{ message: string }> {
  return api.patch<{ message: string }>(`/v1/mfa/passkeys/${encodeURIComponent(passkeyId)}`, {
    name,
  });
}

export function deletePasskey(
  passkeyId: string,
  stepUp?: StepUpProof,
): Promise<DeletePasskeyResponse> {
  return api.delete<DeletePasskeyResponse>(
    `/v1/mfa/passkeys/${encodeURIComponent(passkeyId)}`,
    stepUp ? { body: { stepUp } } : undefined,
  );
}

export function regenerateRecoveryCodes(stepUp?: StepUpProof): Promise<RecoveryCodesResponse> {
  return api.post<RecoveryCodesResponse>("/v1/mfa/recovery-codes", stepUp ? { stepUp } : undefined);
}
